import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, useSearchParams, useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlatformBrandingProvider, usePlatformBranding } from './context/PlatformBrandingContext';
import { PortalProvider } from './context/PortalContext';
import { ThemeProvider } from './context/ThemeContext';
import { getPostLoginPath, canAccessEnquiries, needsSubscriptionGate, isSubscriptionGateAllowedPath } from './utils/rolePaths';
import { Toaster } from './components/ui/toaster';
import { useToast } from './hooks/use-toast';
import { isMobileContainer } from './utils/device';
import useAcademicYearBootstrap from './hooks/useAcademicYearBootstrap';
import api from './utils/api';

// Components
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Students from './components/Students';
import StudentProfile from './components/StudentProfile';
import Teachers from './components/Teachers';
import TeacherProfile from './components/TeacherProfile';
import Classes from './components/Classes';
import ClassDetail from './components/ClassDetail';
import Subjects from './components/Subjects';
import SubjectDetail from './components/SubjectDetail';
import Attendance from './components/Attendance';
import LeaveManagementPage from './components/leave/LeaveManagementPage';
import Fees from './components/Fees';
import ParentAccountsList from './components/ParentAccountsList';
import TeacherAttendance from './components/TeacherAttendance';
import ParentAccountDetail from './components/ParentAccountDetail';
import FeeAccountDues from './components/FeeAccountDues';
import Library from './components/Library';
import Transport from './components/Transport';
import ReportCardPage from './components/reports/ReportCardPage';
import ResultPage from './components/reports/ResultPage';
import TransferCertificatePage from './components/reports/TransferCertificatePage';
import CertificatePage from './components/reports/CertificatePage';
import IdCardPage from './components/reports/IdCardPage';
import AttendanceReportPage from './components/reports/AttendanceReportPage';
import { PORTAL_ROLES } from './utils/navPermissions';
import Exams from './components/Exams';
import ExamDetail from './components/exams/ExamDetail';
import ExamResultsEntry from './components/exams/ExamResultsEntry';
import ExamResultsDashboard from './components/exams/ExamResultsDashboard';
import Timetable from './components/Timetable';
import CalendarEvents from './components/CalendarEvents';
import Announcements from './components/Announcements';
import WhatsappBroadcast from './components/WhatsappBroadcast';
import WhatsappLogsPage from './components/reports/WhatsappLogsPage';
import Settings from './components/Settings';
import Schools from './components/Schools';
import SchoolDetail from './components/SchoolDetail';
import Bin from './components/Bin';
import TopNav from './components/TopNav';
import AppFooter from './components/AppFooter';
import MobileNav from './components/MobileNav';
import PullToRefresh from './components/mobile/PullToRefresh';
import Profile from './components/Profile';
import Search from './components/Search';
import SsoCallback from './components/SsoCallback';
import Landing from './components/landing-page';
import CrmDashboard from './components/crm/CrmDashboard';
import CrmEmployees from './components/crm/CrmEmployees';
import CrmEmployeeProfile from './components/crm/CrmEmployeeProfile';
import CrmInquiries from './components/crm/CrmInquiries';
import CrmInquiryDetail from './components/crm/CrmInquiryDetail';
import CrmPayroll from './components/crm/payroll/CrmPayroll';
import CrmEmployeeAttendance from './components/crm/CrmEmployeeAttendance';
import CrmReports from './components/crm/CrmReports';
import CrmPlans from './components/crm/CrmPlans';
import CrmTickets from './components/crm/tickets/CrmTickets';
import CrmTicketDetail from './components/crm/tickets/CrmTicketDetail';
import CrmTicketReports from './components/crm/tickets/CrmTicketReports';
import CrmMail from './components/crm/mail/CrmMail';
import SubscriptionPlans from './components/subscription/SubscriptionPlans';
import SubscriptionAnalytics from './components/subscription/SubscriptionAnalytics';
import SchoolSubscription from './components/subscription/SchoolSubscription';
import PlanComparison from './components/subscription/PlanComparison';
import SubscriptionPayments from './components/subscription/SubscriptionPayments';
import ModuleUpgradeDialog from './components/subscription/ModuleUpgradeDialog';
import { subscriptionBannerMessage, isOnFreePlan } from './utils/subscriptionStatus';
import { subscriptionModuleForPath, moduleLabel } from './utils/moduleAccess';
import ReceptionDashboard from './components/reception/ReceptionDashboard';
import ReceptionInquiries from './components/reception/ReceptionInquiries';
import ReceptionInquiryDetail from './components/reception/ReceptionInquiryDetail';
import ReceptionVisitors from './components/reception/ReceptionVisitors';
import ReceptionPhoneCalls from './components/reception/ReceptionPhoneCalls';
import ReceptionComplaints from './components/reception/ReceptionComplaints';
import ReceptionAppointments from './components/reception/ReceptionAppointments';
import ReceptionFollowups from './components/reception/ReceptionFollowups';
import ReceptionReports from './components/reception/ReceptionReports';
import { isCrmRole, canManageCrmEmployees, canManageCrmSalaries, canViewCrmReports, canMarkCrmAttendance, canAccessCrmChat, hasPlatformPermission, canManageAssignedFeedback, hasPermission } from './utils/crmPermissions';
import CrmDepartments from './components/crm/CrmDepartments';
import CrmRoles from './components/crm/CrmRoles';
import CrmRolePermissions from './components/crm/CrmRolePermissions';
import CrmAccessDenied from './components/crm/CrmAccessDenied';
import PublicStudentCard from './components/PublicStudentCard';
import PublicInvoicePortal from './components/PublicInvoicePortal';
import { parseLegacyInvoicePortalHash } from './utils/documentPortalUrl';
import PortalStudentAcademics from './components/portal/student/PortalStudentAcademics';
import PortalTimetable from './components/portal/PortalTimetable';
import PortalHomework from './components/portal/PortalHomework';
import PortalStudentExams from './components/portal/student/PortalStudentExams';
import PortalStudentMarks from './components/portal/student/PortalStudentMarks';
import PortalStudentReportCard from './components/portal/student/PortalStudentReportCard';
import PortalStudentAttendance from './components/portal/student/PortalStudentAttendance';
import PortalStudentFees from './components/portal/student/PortalStudentFees';
import PortalStudentProfile from './components/portal/student/PortalStudentProfile';
import PortalParentChildren from './components/portal/parent/PortalParentChildren';
import PortalParentMessages from './components/portal/parent/PortalParentMessages';
import AssignmentsStaff from './components/AssignmentsStaff';
import AppErrorBoundary from './components/AppErrorBoundary';
import PushNotificationBridge from './components/PushNotificationBridge';
import FloatingFeedbackButton from './components/feedback/FloatingFeedbackButton';
import FeedbackSubmitModal from './components/feedback/FeedbackSubmitModal';
import MyFeedbackList from './components/feedback/MyFeedbackList';
import MyFeedbackDetail from './components/feedback/MyFeedbackDetail';
import FeedbackManagement from './components/feedback/FeedbackManagement';
import FeedbackDetail from './components/feedback/FeedbackDetail';
import { canSubmitFeedback, canManageSchoolFeedback } from './utils/feedbackPermissions';
import CrmChatWidget from './components/crm/chat/CrmChatWidget';

const bootstrapSpinnerStyle = {
  border: '3px solid #e5e7eb',
  borderTopColor: '#3b82f6',
  borderRadius: '50%',
  width: 40,
  height: 40,
  animation: 'spin 0.8s linear infinite',
};

function isPublicAppPath() {
  if (typeof window === 'undefined') return false;
  const hash = window.location.hash || '';
  const path = hash.replace(/^#/, '').split('?')[0] || '/';
  return path === '/invoice-portal'
    || path.startsWith('/student-card/')
    || path === '/sso';
}

function AuthBootstrapGate({ children }) {
  const { bootstrapping } = useAuth();
  if (bootstrapping && !isPublicAppPath()) {
    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"
          style={bootstrapSpinnerStyle}
        />
      </div>
    );
  }
  return children;
}

function FeedbackSubmitterRoute({ children }) {
  const { user } = useAuth();
  if (!canSubmitFeedback(user)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  return children;
}

function SchoolFeedbackRoute({ children }) {
  const { user } = useAuth();
  if (!canManageSchoolFeedback(user)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  return children;
}

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function PlatformPermissionRoute({ permission, children }) {
  const { user } = useAuth();
  if (user?.school_observer_mode) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!isCrmRole(user) && user?.role_slug !== 'super_admin') {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  if (!hasPlatformPermission(user, permission)) {
    return <Navigate to="/crm/dashboard" replace />;
  }
  return children;
}

function PlatformFeedbackDetailRoute({ children }) {
  const { user } = useAuth();
  if (hasPlatformPermission(user, 'manage_platform_feedback')) {
    return children;
  }
  return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
}

function CrmAssignedFeedbackRoute({ children }) {
  const { user } = useAuth();
  if (!canManageAssignedFeedback(user)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  return children;
}

function CrmRoute({ children, requireAdmin = false, requireSalaries = false, requireReports = false, requireAttendance = false, requirePermission = null, requireModule = null, requireAction = 'read' }) {
  const { user } = useAuth();
  if (user?.school_observer_mode) {
    return <Navigate to="/dashboard" replace />;
  }
  if (!isCrmRole(user)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  if (requireAdmin && !canManageCrmEmployees(user)) {
    return <Navigate to="/crm/access-denied" replace />;
  }
  if (requireSalaries && !canManageCrmSalaries(user) && !isCrmRole(user)) {
    return <Navigate to="/crm/access-denied" replace />;
  }
  if (requireReports && !canViewCrmReports(user)) {
    return <Navigate to="/crm/access-denied" replace />;
  }
  if (requireAttendance && !canMarkCrmAttendance(user)) {
    return <Navigate to="/crm/access-denied" replace />;
  }
  if (requirePermission && !hasPlatformPermission(user, requirePermission)) {
    return <Navigate to="/crm/access-denied" replace />;
  }
  if (requireModule && !hasPermission(user, requireModule, requireAction)) {
    return <Navigate to="/crm/access-denied" replace />;
  }
  return children;
}

function EnquiriesAccessRoute({ children }) {
  const { user } = useAuth();
  if (!canAccessEnquiries(user?.role_slug)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  return children;
}

function LegacyTeacherRedirect({ edit = false }) {
  const { id } = useParams();
  const target = edit ? `/hr/teachers/${id}/edit` : `/hr/teachers/${id}`;
  return <Navigate to={target} replace />;
}

function LockedModuleRedirect({ moduleKey, label }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  useEffect(() => {
    navigate('/dashboard', { replace: true });
  }, [navigate]);

  return (
    <ModuleUpgradeDialog
      open={open}
      moduleKey={moduleKey}
      moduleLabel={label}
      onClose={() => setOpen(false)}
    />
  );
}

function PermissionRoute({ path, children }) {
  const { user, canAccessPath, canNavigatePath, isModuleLocked } = useAuth();

  if (path === '/events' && PORTAL_ROLES.includes(user?.role_slug)) {
    return (
      <Navigate
        to={user.role_slug === 'parent' ? '/portal/parent/attendance' : '/portal/student/attendance'}
        replace
      />
    );
  }

  if (path && isModuleLocked(path)) {
    const moduleKey = subscriptionModuleForPath(path);
    return (
      <LockedModuleRedirect
        moduleKey={moduleKey}
        label={moduleLabel(moduleKey)}
      />
    );
  }

  if (!canNavigatePath(path) && !canAccessPath(path)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  if (!canAccessPath(path)) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  return children;
}

function SettingsAccessRoute({ children }) {
  const { user, canOpenSettings } = useAuth();
  if (!canOpenSettings()) {
    return <Navigate to={getPostLoginPath(user?.role_slug, user)} replace />;
  }
  return children;
}

function DashboardScreen() {
  const { user } = useAuth();
  if (isCrmRole(user) && !user?.school_observer_mode) {
    return <Navigate to="/crm/dashboard" replace />;
  }
  if (user?.role_slug === 'receptionist') {
    return <Navigate to="/reception" replace />;
  }
  if (needsSubscriptionGate(user)) {
    return <Navigate to="/settings/subscription/upgrade" replace />;
  }
  return (
    <Layout>
      <Dashboard />
    </Layout>
  );
}

// Layout Component with Sidebar + Top Navbar (desktop) or mobile-friendly layout
const Layout = ({ children }) => {
  const { user, exitSchoolObserver, isSchoolObserver } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { branding: platformBranding } = usePlatformBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [schoolBranding, setSchoolBranding] = useState(null);
  const [exitingObserver, setExitingObserver] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const isMobile = isMobileContainer();
  useAcademicYearBootstrap(user?.school_id);

  const handlePullRefresh = () =>
    new Promise((resolve) => {
      setRefreshKey((k) => k + 1);
      setTimeout(resolve, 350);
    });

  useEffect(() => {
    if (isSchoolObserver && user?.school_id) {
      let cancelled = false;
      api
        .get(`schools/${user.school_id}`)
        .then((res) => {
          if (cancelled || !res.data?.data) return;
          const d = res.data.data;
          setSchoolBranding({ id: d.id ?? user.school_id, name: d.name || user.observer_school_name || 'School', logo: d.logo || null });
        })
        .catch(() => {
          if (!cancelled) {
            setSchoolBranding({
              id: user.school_id,
              name: user.observer_school_name || 'School',
              logo: null,
            });
          }
        });
      return () => { cancelled = true; };
    }
    if (user?.role_slug === 'super_admin') {
      setSchoolBranding(null);
      return undefined;
    }
    const sid = user?.school_id;
    if (!sid) {
      setSchoolBranding(null);
      return;
    }
    let cancelled = false;
    api
      .get(`schools/${sid}`)
      .then((res) => {
        if (cancelled || !res.data?.data) return;
        const d = res.data.data;
        setSchoolBranding({ id: d.id ?? sid, name: d.name || 'School', logo: d.logo || null });
      })
      .catch(() => {
        if (!cancelled) setSchoolBranding(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.school_id, user?.role_slug, user?.observer_school_name, isSchoolObserver]);

  const handleExitObserver = async () => {
    setExitingObserver(true);
    try {
      const result = await exitSchoolObserver();
      if (result.success) {
        navigate('/crm/dashboard', { replace: true });
      } else {
        toast({
          title: 'Could not exit school view',
          description: result.message || 'Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setExitingObserver(false);
    }
  };

  const isSuperAdmin = user?.role_slug === 'super_admin' && !isSchoolObserver;
  const footerPlatformBranding = isSuperAdmin ? platformBranding : null;

  if (needsSubscriptionGate(user) && !isSubscriptionGateAllowedPath(location.pathname)) {
    return <Navigate to="/settings/subscription/upgrade" replace />;
  }

  return (
    <div className={`min-h-screen bg-background text-foreground pb-0 md:pb-0 ${isMobile ? 'mobile-shell' : ''}`}>
      <Sidebar
        isOpen={sidebarOpen}
        setIsOpen={setSidebarOpen}
        schoolBranding={schoolBranding}
        platformBranding={platformBranding}
      />
      <div className={isMobile ? 'min-h-screen flex flex-col' : 'lg:ml-64 min-h-screen flex flex-col'}>
        <TopNav
          isSidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          schoolBranding={schoolBranding}
          platformBranding={platformBranding}
        />
        <main className={`flex-1 ${isMobile ? 'pb-[var(--mobile-nav-offset)]' : 'pb-0 md:pb-0'}`}>
          {isSchoolObserver && (
            <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-sm text-foreground">
                Viewing <span className="font-semibold">{user?.observer_school_name || schoolBranding?.name || 'school'}</span> — read-only
              </p>
              <button
                type="button"
                onClick={handleExitObserver}
                disabled={exitingObserver}
                className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50"
              >
                {exitingObserver ? 'Exiting…' : 'Exit to platform'}
              </button>
            </div>
          )}
          {!isSchoolObserver && user?.role_slug === 'school_admin' && subscriptionBannerMessage(user) && !isOnFreePlan(user) && (
            <div className="bg-amber-500/15 border-b border-amber-500/30 px-4 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-sm text-foreground">{subscriptionBannerMessage(user)}</p>
              <a href="#/settings/subscription/upgrade" className="text-sm font-medium text-blue-600 hover:underline shrink-0">
                Upgrade plan
              </a>
            </div>
          )}
          <div className={isMobile ? 'p-4' : 'p-4 lg:p-8'}>
            {isMobile ? (
              <PullToRefresh onRefresh={handlePullRefresh}>
                <div key={refreshKey}>{children}</div>
              </PullToRefresh>
            ) : (
              children
            )}
          </div>
          <AppFooter platformBranding={footerPlatformBranding} />
        </main>
        {isMobile && (
          <MobileNav
            onOpenMenu={() => setSidebarOpen(true)}
            menuOpen={sidebarOpen}
          />
        )}
        {canSubmitFeedback(user) && (
          <>
            <FloatingFeedbackButton onClick={() => setFeedbackOpen(true)} />
            <FeedbackSubmitModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
          </>
        )}
        {canAccessCrmChat(user) && (
          <CrmChatWidget hasFeedbackButton={canSubmitFeedback(user)} />
        )}
      </div>
    </div>
  );
};

function InvoicePortalRoute() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')
    || parseLegacyInvoicePortalHash(window.location.hash);
  return <PublicInvoicePortal token={token} />;
}

function AppRoutes() {
  const { isAuthenticated, user } = useAuth();
  const isMobile = isMobileContainer();
  const authedHome = getPostLoginPath(user?.role_slug, user);

  return (
    <Routes>
      <Route path="/sso" element={<SsoCallback />} />
      <Route path="/student-card/:id" element={<PublicStudentCard />} />
      <Route path="/invoice-portal" element={<InvoicePortalRoute />} />
      <Route
        path="/"
        element={
          isAuthenticated
            ? <Navigate to={authedHome} replace />
            : (isMobile ? <Navigate to="/login" replace /> : <Landing />)
        }
      />
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to={authedHome} replace /> : <Login />} 
      />
      <Route
        path="/register/*"
        element={isAuthenticated ? <Navigate to={authedHome} replace /> : <Register />}
      />
      <Route
        path="/forgot-password"
        element={isAuthenticated ? <Navigate to={authedHome} replace /> : <ForgotPassword />}
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardScreen />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/schools"
        element={
          <ProtectedRoute>
            <PlatformPermissionRoute permission="manage_schools">
              <Layout><Schools /></Layout>
            </PlatformPermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/bin"
        element={
          <ProtectedRoute>
            <PlatformPermissionRoute permission="manage_bin">
              <Layout><Bin /></Layout>
            </PlatformPermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route path="/schools/bin" element={<Navigate to="/bin?tab=schools" replace />} />
      <Route
        path="/schools/:id"
        element={
          <ProtectedRoute>
            <PlatformPermissionRoute permission="manage_schools">
              <Layout><SchoolDetail /></Layout>
            </PlatformPermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/subscription/plans"
        element={
          <ProtectedRoute>
            <PlatformPermissionRoute permission="manage_subscription_plans">
              <Layout><SubscriptionPlans /></Layout>
            </PlatformPermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/analytics"
        element={
          <ProtectedRoute>
            <PlatformPermissionRoute permission="manage_subscriptions">
              <Layout><SubscriptionAnalytics /></Layout>
            </PlatformPermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <PlatformPermissionRoute permission="manage_platform_feedback">
              <Layout><FeedbackManagement scope="platform" /></Layout>
            </PlatformPermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback/:id"
        element={
          <ProtectedRoute>
            <PlatformFeedbackDetailRoute>
              <Layout><FeedbackDetail scope="platform" /></Layout>
            </PlatformFeedbackDetailRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/school-feedback"
        element={
          <ProtectedRoute>
            <SchoolFeedbackRoute>
              <Layout><FeedbackManagement scope="school" /></Layout>
            </SchoolFeedbackRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/school-feedback/:id"
        element={
          <ProtectedRoute>
            <SchoolFeedbackRoute>
              <Layout><FeedbackDetail scope="school" /></Layout>
            </SchoolFeedbackRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/my-feedback"
        element={
          <ProtectedRoute>
            <FeedbackSubmitterRoute>
              <Layout><MyFeedbackList /></Layout>
            </FeedbackSubmitterRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-feedback/:id"
        element={
          <ProtectedRoute>
            <FeedbackSubmitterRoute>
              <Layout><MyFeedbackDetail /></Layout>
            </FeedbackSubmitterRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/subscription"
        element={
          <ProtectedRoute>
            <Layout><SchoolSubscription /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/subscription/upgrade"
        element={
          <ProtectedRoute>
            <Layout><PlanComparison /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/subscription/payments"
        element={
          <ProtectedRoute>
            <Layout><SubscriptionPayments /></Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/crm/dashboard"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmDashboard /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/access-denied"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmAccessDenied /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/departments"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmDepartments /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/roles"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmRoles /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/roles/:id/permissions"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmRolePermissions /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/employees"
        element={
          <ProtectedRoute>
            <CrmRoute requireAdmin>
              <Layout><CrmEmployees /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/employees/:id"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmEmployeeProfile /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/inquiries"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmInquiries /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/inquiries/:id"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmInquiryDetail /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/feedback"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <CrmAssignedFeedbackRoute>
                <Layout><FeedbackManagement scope="crm" /></Layout>
              </CrmAssignedFeedbackRoute>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/feedback/:id"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <CrmAssignedFeedbackRoute>
                <Layout><FeedbackDetail scope="crm" /></Layout>
              </CrmAssignedFeedbackRoute>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/tickets/reports"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmTicketReports /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/tickets/:id"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmTicketDetail /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/tickets"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmTickets /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/mail"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmMail /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/plans"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmPlans /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/salaries"
        element={
          <ProtectedRoute>
            <CrmRoute>
              <Layout><CrmPayroll /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/attendance"
        element={
          <ProtectedRoute>
            <CrmRoute requireAttendance>
              <Layout><CrmEmployeeAttendance /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/crm/reports"
        element={
          <ProtectedRoute>
            <CrmRoute requireReports>
              <Layout><CrmReports /></Layout>
            </CrmRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/enquiries"
        element={<Navigate to="/crm/inquiries" replace />}
      />
      
      <Route
        path="/students"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/students">
              <Layout><Students /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:id/edit"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/students">
              <Layout><StudentProfile editMode /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/students/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/students">
              <Layout><StudentProfile /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route path="/reception" element={<ProtectedRoute><PermissionRoute path="/reception"><Layout><ReceptionDashboard /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/inquiries" element={<ProtectedRoute><PermissionRoute path="/reception/inquiries"><Layout><ReceptionInquiries /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/inquiries/:id" element={<ProtectedRoute><PermissionRoute path="/reception/inquiries"><Layout><ReceptionInquiryDetail /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/visitors" element={<ProtectedRoute><PermissionRoute path="/reception/visitors"><Layout><ReceptionVisitors /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/phone-calls" element={<ProtectedRoute><PermissionRoute path="/reception/phone-calls"><Layout><ReceptionPhoneCalls /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/complaints" element={<ProtectedRoute><PermissionRoute path="/reception/complaints"><Layout><ReceptionComplaints /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/appointments" element={<ProtectedRoute><PermissionRoute path="/reception/appointments"><Layout><ReceptionAppointments /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/followups" element={<ProtectedRoute><PermissionRoute path="/reception/followups"><Layout><ReceptionFollowups /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/reception/reports" element={<ProtectedRoute><PermissionRoute path="/reception/reports"><Layout><ReceptionReports /></Layout></PermissionRoute></ProtectedRoute>} />
      
      <Route
        path="/hr/teachers"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/hr/teachers">
              <Layout><Teachers /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/teachers/attendance"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/teachers/attendance">
              <Layout><TeacherAttendance /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/teachers/:id/edit"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/hr/teachers">
              <Layout><TeacherProfile editMode /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr/teachers/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/hr/teachers">
              <Layout><TeacherProfile /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route path="/teachers/:id/edit" element={<LegacyTeacherRedirect edit />} />
      <Route path="/teachers/:id" element={<LegacyTeacherRedirect />} />
      <Route path="/teachers" element={<Navigate to="/hr/teachers" replace />} />
      
      <Route
        path="/subjects/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/subjects">
              <Layout><SubjectDetail /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subjects"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/subjects">
              <Layout><Subjects /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/classes/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/classes">
              <Layout><ClassDetail /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/classes"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/classes">
              <Layout><Classes /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/attendance"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/attendance">
              <Layout><Attendance /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/leave">
              <Layout><LeaveManagementPage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/timetable"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/timetable">
              <Layout><Timetable /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/exams"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/exams">
              <Layout><Exams /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/exams/:examId/results"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/exams">
              <Layout><ExamResultsEntry /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/exams/:examId/results/analytics"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/exams">
              <Layout><ExamResultsDashboard /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/exams/:examId"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/exams">
              <Layout><ExamDetail /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/fees/accounts/dues"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/fees">
              <Layout><FeeAccountDues /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fees/accounts/students"
        element={
          <ProtectedRoute>
            <Navigate to="/fees/accounts" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/fees/accounts/:id"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/fees">
              <Layout><ParentAccountDetail /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fees/accounts"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/fees">
              <Layout><ParentAccountsList /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fees/management"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/fees">
              <Layout><Fees /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/fees"
        element={
          <ProtectedRoute>
            <Navigate to="/fees/management" replace />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/library">
              <Layout><Library /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/transport"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/transport">
              <Layout><Transport /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports/report-card"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/report-card">
              <Layout><ReportCardPage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/attendance"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/attendance">
              <Layout><AttendanceReportPage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/result"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/result">
              <Layout><ResultPage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/transfer-certificate"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/transfer-certificate">
              <Layout><TransferCertificatePage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/certificate"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/certificate">
              <Layout><CertificatePage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/id-card"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/id-card">
              <Layout><IdCardPage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/events"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/events">
              <Layout><CalendarEvents /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/announcements"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/announcements">
              <Layout><Announcements /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/whatsapp/broadcast"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/whatsapp/broadcast">
              <Layout><WhatsappBroadcast /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports/whatsapp-logs"
        element={
          <ProtectedRoute>
            <PermissionRoute path="/reports/whatsapp-logs">
              <Layout><WhatsappLogsPage /></Layout>
            </PermissionRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsAccessRoute>
              <Layout><Settings /></Layout>
            </SettingsAccessRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout><Profile /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Student portal */}
      <Route path="/portal/student/academics" element={<ProtectedRoute><PermissionRoute path="/portal/student/academics"><Layout><PortalStudentAcademics /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/timetable" element={<ProtectedRoute><PermissionRoute path="/portal/student/timetable"><Layout><PortalTimetable /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/homework" element={<ProtectedRoute><PermissionRoute path="/portal/student/homework"><Layout><PortalHomework /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/exams" element={<ProtectedRoute><PermissionRoute path="/portal/student/exams"><Layout><PortalStudentExams /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/marks" element={<ProtectedRoute><PermissionRoute path="/portal/student/marks"><Layout><PortalStudentMarks /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/report-card" element={<ProtectedRoute><PermissionRoute path="/portal/student/report-card"><Layout><PortalStudentReportCard /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/attendance" element={<ProtectedRoute><PermissionRoute path="/portal/student/attendance"><Layout><PortalStudentAttendance /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/leave" element={<ProtectedRoute><PermissionRoute path="/portal/student/leave"><Layout><PortalStudentAttendance leavePage /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/fees" element={<ProtectedRoute><PermissionRoute path="/portal/student/fees"><Layout><PortalStudentFees /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/student/calendar" element={<Navigate to="/portal/student/attendance" replace />} />
      <Route path="/portal/student/profile" element={<ProtectedRoute><PermissionRoute path="/portal/student/profile"><Layout><PortalStudentProfile /></Layout></PermissionRoute></ProtectedRoute>} />

      {/* Parent portal */}
      <Route path="/portal/parent/children" element={<ProtectedRoute><PermissionRoute path="/portal/parent/children"><Layout><PortalParentChildren /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/child/:studentId" element={<ProtectedRoute><PermissionRoute path="/portal/parent/children"><Layout><PortalStudentProfile parentMode /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/attendance" element={<ProtectedRoute><PermissionRoute path="/portal/parent/attendance"><Layout><PortalStudentAttendance parentMode /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/leave" element={<ProtectedRoute><PermissionRoute path="/portal/parent/leave"><Layout><PortalStudentAttendance parentMode leavePage /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/homework" element={<ProtectedRoute><PermissionRoute path="/portal/parent/homework"><Layout><PortalHomework /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/timetable" element={<ProtectedRoute><PermissionRoute path="/portal/parent/timetable"><Layout><PortalTimetable /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/exams" element={<ProtectedRoute><PermissionRoute path="/portal/parent/exams"><Layout><PortalStudentExams /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/fees" element={<ProtectedRoute><PermissionRoute path="/portal/parent/fees"><Layout><PortalStudentFees parentMode /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/messages" element={<ProtectedRoute><PermissionRoute path="/portal/parent/messages"><Layout><PortalParentMessages /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route path="/portal/parent/calendar" element={<Navigate to="/portal/parent/attendance" replace />} />

      <Route path="/assignments" element={<ProtectedRoute><PermissionRoute path="/assignments"><Layout><AssignmentsStaff /></Layout></PermissionRoute></ProtectedRoute>} />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            <Layout><Search /></Layout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="*"
        element={
          isAuthenticated
            ? <Navigate to={authedHome} replace />
            : (isMobile ? <Navigate to="/login" replace /> : <Navigate to="/" replace />)
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <Router>
        <AuthProvider>
          <PlatformBrandingProvider>
            <PortalProvider>
              <ThemeProvider>
                <AuthBootstrapGate>
                  <PushNotificationBridge />
                  <AppRoutes />
                </AuthBootstrapGate>
                <Toaster />
              </ThemeProvider>
            </PortalProvider>
          </PlatformBrandingProvider>
        </AuthProvider>
      </Router>
    </AppErrorBoundary>
  );
}

export default App;
